import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import * as yup from 'yup';
import { AlertState, convertAbelCurrency, Currency, formatBalanceToAbel, ThemeMode } from '../../../abelcommon';
import { LoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { selectedMiningAccountState } from '../../../api';
import { loadBalancerAPI } from '../../../api/load-balancer';
import { useTranslation } from 'react-i18next';

export const OverviewSection = () => {
  const theme = useTheme();

  const selectedMiningAccount = useRecoilValue(selectedMiningAccountState);
  const {
    data: payoutOverview,
    isLoading: getPayoutOverviewLoading,
    error: getPayoutOverviewError,
    refetch: refetchPayoutOverview,
  } = loadBalancerAPI.hookWrapperQuery.useGetPayoutOverview(selectedMiningAccount.id);

  const [milisecTimeCounter, setMilisecTimeCounter] = useState(0);
  const { t } = useTranslation();
  const [isShowUpdatePayoutThresholdModal, setIsShowUpdatePayoutThresholdModal] = useState(false);
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    severity: 'success',
    message: '',
  });

  useEffect(() => {
    if (getPayoutOverviewLoading || !payoutOverview) return;

    setMilisecTimeCounter(payoutOverview.nextDistribution.nextDistributionTimeInSecond * 1000);
  }, [payoutOverview, getPayoutOverviewLoading]);

  const nextDistributionTime = useMemo(() => {
    if (milisecTimeCounter <= 0) {
      return {
        hours: '00',
        minutes: '00',
        seconds: '00',
      };
    }

    const diffInHours = Math.floor(milisecTimeCounter / (1000 * 60 * 60));
    const diffInMinutes = Math.floor((milisecTimeCounter / (1000 * 60)) % 60);
    const diffInSecond = Math.floor((milisecTimeCounter / 1000) % 60);

    return {
      hours: diffInHours === 0 ? '00' : `${diffInHours < 10 ? '0' : ''}${diffInHours}`,
      minutes: diffInMinutes === 0 ? '00' : `${diffInMinutes < 10 ? '0' : ''}${diffInMinutes}`,
      seconds: diffInSecond === 0 ? '00' : `${diffInSecond < 10 ? '0' : ''}${diffInSecond}`,
    };
  }, [milisecTimeCounter]);

  const overviewData = useMemo(
    () => [
      {
        key: 'balance',
        title: t('overviewSectionPage.balance'),
        value: !payoutOverview
          ? 0
          : formatBalanceToAbel(
              payoutOverview.miningAccount.minerBalance.balance -
                payoutOverview.miningAccount.minerRedeemedBalance.redeemedBalance -
                payoutOverview.miningAccount.minerRedeemedBalance.gasFee
            ),
        subtitle: t('overviewSectionPage.totalIncomeNotYetRedeemed'),
      },
      {
        key: 'redeemed',
        title: t('overviewSectionPage.redeemed'),
        value: !payoutOverview
          ? 0
          : formatBalanceToAbel(
              payoutOverview.miningAccount.minerRedeemedBalance.redeemedBalance +
                payoutOverview.miningAccount.minerRedeemedBalance.gasFee
            ),
        subtitle: `${t('overviewSectionPage.totalPayout')} (ABEL)`,
      },
      {
        key: 'totalMined',
        title: t('overviewSectionPage.totalMined'),
        value: !payoutOverview ? 0 : formatBalanceToAbel(payoutOverview.miningAccount.minerBalance.balance),
        subtitle: `${t('overviewSectionPage.totalMined')} (ABEL)`,
      },
      {
        key: 'nextDistribution',
        title: t('overviewSectionPage.nextPayout'),
        value: `@${t('overviewSectionPage.block')} ${!payoutOverview ? 0 : payoutOverview.nextDistribution.currentBlockHeight}`,
        subtitle: `${t('overviewSectionPage.estimatedTime')}: ${nextDistributionTime.hours} ${t('overviewSectionPage.hours')} ${nextDistributionTime.minutes} ${t('overviewSectionPage.mins')} ${nextDistributionTime.seconds} ${t('overviewSectionPage.secs')}`,
      },
    ],
    [t, payoutOverview, nextDistributionTime.hours, nextDistributionTime.minutes, nextDistributionTime.seconds]
  );

  return (
    <Card>
      <CardHeader
        title={t('overviewSectionPage.overview')}
        sx={{ flexWrap: 'wrap' }}
        action={
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            {getPayoutOverviewLoading ? (
              <Skeleton variant="rectangular" sx={{ height: '24px', width: '300px', borderRadius: 1 }} />
            ) : (
              <>
                <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="body1" color="appGray.contrastText" sx={{ mr: 1 }}>
                    {t('overviewSectionPage.payoutThreshold')}:
                  </Typography>
                  <Tooltip title={t('overviewSectionPage.updatePayoutThreshold')}>
                    <IconButton aria-label="edit threshold" onClick={() => setIsShowUpdatePayoutThresholdModal(true)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body1" color="success.main">
                    {formatBalanceToAbel(payoutOverview.miningAccount.payoutThreshold)} ABEL
                  </Typography>
                </Stack>
                <IconButton aria-label="reload" onClick={() => refetchPayoutOverview()}>
                  <CachedIcon />
                </IconButton>
              </>
            )}
          </Stack>
        }
      />
      <CardContent sx={{ overflow: 'auto' }}>
        <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
          {getPayoutOverviewError && (
            <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0 }}>
              {getPayoutOverviewError.message}
            </Alert>
          )}
          {overviewData.map(({ key, title, value, subtitle }) => {
            return (
              <Grid item key={key} xs={12} lg={6} xl={3}>
                {getPayoutOverviewLoading ? (
                  <Skeleton variant="rectangular" sx={{ minHeight: '82px', borderRadius: 1 }} />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      boxShadow: 1,
                      padding: '8px',
                      border: 1,
                      borderColor: theme.palette.appGray.dark,
                      borderRadius: 1,
                      minHeight: '82px',
                      ...(theme.palette.mode !== ThemeMode.Light
                        ? {}
                        : {
                            backgroundColor: theme.palette.primary.light,
                            borderColor: 'transparent',
                            boxShadow: 'none',
                          }),
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 500 }}>
                      {title}
                    </Typography>
                    <Typography variant="h5" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {value}
                    </Typography>
                    {subtitle && (
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {subtitle}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
      {isShowUpdatePayoutThresholdModal && (
        <UpdatePayoutThresholdModal
          miningAccount={payoutOverview.miningAccount}
          onCompleted={setAlertState}
          onClose={() => {
            setIsShowUpdatePayoutThresholdModal(false);
            refetchPayoutOverview();
          }}
        />
      )}
      <Snackbar
        open={alertState.isOpen}
        autoHideDuration={alertState.autoHideDuration === undefined ? 5000 : alertState.autoHideDuration}
        onClose={(e, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setAlertState((prev) => ({ ...prev, isOpen: false }));
        }}
      >
        <Alert
          onClose={() => setAlertState((prev) => ({ ...prev, isOpen: false }))}
          severity={alertState.severity}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

const UpdatePayoutThresholdModal = ({
  miningAccount,
  onCompleted,
  onClose,
}: {
  miningAccount: { id: string; payoutThreshold: number };
  onCompleted: React.Dispatch<React.SetStateAction<AlertState>>;
  onClose: () => void;
}) => {
  const { mutate: updatePayoutThreshold, isLoading: updatePayoutThresholdLoading } =
    loadBalancerAPI.hookWrapperMutation.useUpdatePayoutThreshold();
  const isApiLoading = updatePayoutThresholdLoading;
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      payoutThreshold: convertAbelCurrency(miningAccount.payoutThreshold, Currency.Neutrinos, Currency.Abel),
    },
    validationSchema: yup.object({
      payoutThreshold: yup.number().min(1).max(256).required(t('overviewSectionPage.payoutThresholdIsRequired')),
    }),
    onSubmit: (values) => {
      if (isApiLoading) return;

      updatePayoutThreshold(
        {
          miningAccountId: miningAccount.id,
          payoutThreshold: convertAbelCurrency(values.payoutThreshold, Currency.Abel, Currency.Neutrinos),
        },
        {
          onSuccess: () => {
            onCompleted((prevState) => ({
              ...prevState,
              isOpen: true,
              severity: 'success',
              message: t('overviewSectionPage.updatePayoutThresholdSuccessfully'),
            }));
            onClose();
          },
          onError: (error) => {
            onCompleted((prevState) => ({ ...prevState, isOpen: true, severity: 'error', message: error.message }));
            onClose();
          },
        }
      );
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
        style: { maxWidth: '500px', width: '100%' },
      }}
    >
      <DialogTitle>{t('overviewSectionPage.updatePayoutThreshold')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ py: 4 }}>
        <TextField
          id="payoutThreshold"
          name="payoutThreshold"
          label={t('overviewSectionPage.payoutThreshold')}
          value={formik.values.payoutThreshold}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.payoutThreshold && Boolean(formik.errors.payoutThreshold)}
          helperText={formik.touched.payoutThreshold && formik.errors.payoutThreshold}
          fullWidth
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          type="number"
          inputProps={{
            min: 1,
            max: 256,
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="info" size="small">
          {t('overviewSectionPage.cancel')}
        </Button>
        <Button type="submit" variant="contained" size="small" disabled={isApiLoading}>
          {t('overviewSectionPage.submit')}
        </Button>
      </DialogActions>
      {isApiLoading && <LoadingOverlay />}
    </Dialog>
  );
};
